import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faBell,
  faCheck,
  faFloppyDisk,
  faLink,
  faPaperPlane,
  faRotate,
  faTableColumns,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { getUser, postUser } from "../../helper/http";
import styles from "./DeliveryOperationsPage.module.css";

interface DeliveryModule {
  id: number;
  table_name: string;
  name: string;
}

interface DeliveryAlert {
  id: number;
  order_id: number;
  public_id: string;
  type: string;
  severity: string;
  triggered_at: string;
  contact_name?: string;
  pickup_address?: string;
  courier_name?: string;
}

interface DeliveryOrder {
  id: number;
  public_id: string;
  status: string;
  contact_name?: string;
  pickup_address?: string;
  client_name?: string;
  courier_name?: string;
  review_required: boolean;
  created_at: string;
}

interface Overview {
  updatedAt: string;
  statusCounts: { status: string; count: number }[];
  activeAlerts: DeliveryAlert[];
  recentOrders: DeliveryOrder[];
  courierCounts: { status: string; count: number }[];
  modules: DeliveryModule[];
}

interface TelegramSettings {
  enabled: boolean;
  chatId: string;
  botConfigured: boolean;
  notifyStatusChanges: boolean;
  notifyAlerts: boolean;
  updatedAt?: string;
}

function formatDate(value?: string) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

export default function DeliveryOperationsPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [telegramSettings, setTelegramSettings] =
    useState<TelegramSettings | null>(null);
  const [telegramToken, setTelegramToken] = useState("");
  const [telegramLoading, setTelegramLoading] = useState(true);
  const [telegramSaving, setTelegramSaving] = useState(false);
  const [telegramTesting, setTelegramTesting] = useState(false);
  const [telegramError, setTelegramError] = useState("");
  const [telegramSuccess, setTelegramSuccess] = useState("");
  const [canConfigureTelegram, setCanConfigureTelegram] = useState(true);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getUser("api/delivery/admin/overview");
      setOverview(response);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load delivery operations",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadTelegramSettings();
  }, []);

  const errorMessage = (err: any, fallback: string) =>
    err?.response?.data?.message || err?.message || fallback;

  const loadTelegramSettings = async () => {
    setTelegramLoading(true);
    setTelegramError("");
    try {
      const response = await getUser("api/delivery/admin/telegram-settings");
      setTelegramSettings(response.settings);
      setCanConfigureTelegram(true);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setCanConfigureTelegram(false);
        setTelegramSettings(null);
      } else {
        setTelegramError(errorMessage(err, "Failed to load Telegram settings"));
      }
    } finally {
      setTelegramLoading(false);
    }
  };

  const changeTelegramSettings = (patch: Partial<TelegramSettings>) => {
    setTelegramSettings((current) =>
      current ? { ...current, ...patch } : current,
    );
    setTelegramSuccess("");
  };

  const saveTelegramSettings = async () => {
    if (!telegramSettings || telegramSaving) return;
    setTelegramSaving(true);
    setTelegramError("");
    setTelegramSuccess("");
    try {
      const response = await postUser("api/delivery/admin/telegram-settings", {
        enabled: telegramSettings.enabled,
        chatId: telegramSettings.chatId.trim(),
        notifyStatusChanges: telegramSettings.notifyStatusChanges,
        notifyAlerts: telegramSettings.notifyAlerts,
        ...(telegramToken.trim() ? { botToken: telegramToken.trim() } : {}),
      });
      setTelegramSettings(response.settings);
      setTelegramToken("");
      setTelegramSuccess("Telegram settings saved.");
    } catch (err: any) {
      setTelegramError(errorMessage(err, "Failed to save Telegram settings"));
    } finally {
      setTelegramSaving(false);
    }
  };

  const testTelegramSettings = async () => {
    if (telegramTesting) return;
    setTelegramTesting(true);
    setTelegramError("");
    setTelegramSuccess("");
    try {
      await postUser("api/delivery/admin/telegram-settings/test");
      setTelegramSuccess("Test notification sent.");
    } catch (err: any) {
      setTelegramError(
        errorMessage(err, "Failed to send the test notification"),
      );
    } finally {
      setTelegramTesting(false);
    }
  };

  const modules = useMemo(
    () =>
      Object.fromEntries(
        (overview?.modules || []).map((module) => [module.table_name, module]),
      ),
    [overview],
  );

  const openModule = (tableName: string) => {
    const module = modules[tableName];
    if (module) navigate(`/modules/${module.id}`);
  };

  const acknowledge = async (id: number) => {
    await postUser("api/delivery/admin/alert/acknowledge", { id });
    await load();
  };

  return (
    <main className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1>Delivery Operations</h1>
          <span>
            {overview
              ? `Updated ${formatDate(overview.updatedAt)}`
              : "Current queue state"}
          </span>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => navigate("/app-downloads-settings")}
            title="Configure the Web version address"
          >
            <FontAwesomeIcon icon={faLink} />
            <span>Web address</span>
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => navigate("/delivery-export-columns")}
            title="Configure Excel columns"
          >
            <FontAwesomeIcon icon={faTableColumns} />
            <span>Excel columns</span>
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faRotate} />
            <span>{loading ? "Loading" : "Refresh"}</span>
          </button>
        </div>
      </header>

      {error && <div className={styles.error}>{error}</div>}

      <section className={styles.metrics} aria-label="Order status summary">
        {(overview?.statusCounts || []).map((item) => (
          <button
            type="button"
            key={item.status}
            onClick={() => openModule("delivery_order")}
          >
            <strong>{item.count}</strong>
            <span>{item.status.replace(/_/g, " ")}</span>
          </button>
        ))}
        {!loading && !overview?.statusCounts.length && (
          <div className={styles.empty}>No delivery orders yet.</div>
        )}
      </section>

      {canConfigureTelegram && (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <div>
              <h2>Telegram Notifications</h2>
              <span>
                {telegramSettings?.botConfigured
                  ? "Bot configured"
                  : "Bot not configured"}
              </span>
            </div>
            <FontAwesomeIcon className={styles.sectionIcon} icon={faBell} />
          </div>

          {telegramError && <div className={styles.error}>{telegramError}</div>}
          {telegramSuccess && (
            <div className={styles.success}>{telegramSuccess}</div>
          )}

          {telegramLoading || !telegramSettings ? (
            <div className={styles.empty}>Loading Telegram settings...</div>
          ) : (
            <div className={styles.telegramForm}>
              <label className={styles.field}>
                <span>Telegram group ID or @channel</span>
                <input
                  type="text"
                  value={telegramSettings.chatId}
                  onChange={(event) =>
                    changeTelegramSettings({ chatId: event.target.value })
                  }
                  placeholder="-1001234567890"
                  disabled={telegramSaving}
                />
              </label>

              <label className={styles.field}>
                <span>Bot token</span>
                <input
                  type="password"
                  value={telegramToken}
                  onChange={(event) => setTelegramToken(event.target.value)}
                  placeholder={
                    telegramSettings.botConfigured
                      ? "Configured - enter a new token to replace it"
                      : "Enter Telegram bot token"
                  }
                  autoComplete="new-password"
                  disabled={telegramSaving}
                />
              </label>

              <div className={styles.toggles}>
                <label>
                  <input
                    className={styles.toggleInput}
                    type="checkbox"
                    checked={telegramSettings.enabled}
                    onChange={(event) =>
                      changeTelegramSettings({ enabled: event.target.checked })
                    }
                    disabled={telegramSaving}
                  />
                  <span className={styles.toggleControl} aria-hidden="true">
                    {telegramSettings.enabled && (
                      <FontAwesomeIcon icon={faCheck} />
                    )}
                  </span>
                  <span>Enabled</span>
                </label>
                <label>
                  <input
                    className={styles.toggleInput}
                    type="checkbox"
                    checked={telegramSettings.notifyStatusChanges}
                    onChange={(event) =>
                      changeTelegramSettings({
                        notifyStatusChanges: event.target.checked,
                      })
                    }
                    disabled={telegramSaving}
                  />
                  <span className={styles.toggleControl} aria-hidden="true">
                    {telegramSettings.notifyStatusChanges && (
                      <FontAwesomeIcon icon={faCheck} />
                    )}
                  </span>
                  <span>Status changes</span>
                </label>
                <label>
                  <input
                    className={styles.toggleInput}
                    type="checkbox"
                    checked={telegramSettings.notifyAlerts}
                    onChange={(event) =>
                      changeTelegramSettings({
                        notifyAlerts: event.target.checked,
                      })
                    }
                    disabled={telegramSaving}
                  />
                  <span className={styles.toggleControl} aria-hidden="true">
                    {telegramSettings.notifyAlerts && (
                      <FontAwesomeIcon icon={faCheck} />
                    )}
                  </span>
                  <span>Alerts</span>
                </label>
              </div>

              <div className={styles.formActions}>
                <button
                  type="button"
                  onClick={saveTelegramSettings}
                  disabled={telegramSaving}
                >
                  <FontAwesomeIcon icon={faFloppyDisk} />
                  <span>{telegramSaving ? "Saving" : "Save"}</span>
                </button>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={testTelegramSettings}
                  disabled={
                    telegramTesting ||
                    !telegramSettings.enabled ||
                    !telegramSettings.botConfigured
                  }
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  <span>
                    {telegramTesting ? "Sending" : "Test notification"}
                  </span>
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <h2>Active Alerts</h2>
            <span>{overview?.activeAlerts.length || 0} require attention</span>
          </div>
          <button type="button" onClick={() => openModule("delivery_alert")}>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            <span>Open table</span>
          </button>
        </div>
        <div className={styles.tableWrap}>
          <table>
            <thead>
              <tr>
                <th>Order</th>
                <th>Alert</th>
                <th>Courier</th>
                <th>Triggered</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {(overview?.activeAlerts || []).map((alert) => (
                <tr
                  key={alert.id}
                  className={
                    alert.severity === "critical" ? styles.critical : ""
                  }
                >
                  <td>
                    <strong>{alert.public_id}</strong>
                    <small>
                      {alert.contact_name || alert.pickup_address || "-"}
                    </small>
                  </td>
                  <td>
                    <FontAwesomeIcon icon={faTriangleExclamation} />{" "}
                    {alert.type.replace(/_/g, " ")}
                  </td>
                  <td>{alert.courier_name || "Unassigned"}</td>
                  <td>{formatDate(alert.triggered_at)}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.iconButton}
                      onClick={() => acknowledge(alert.id)}
                      title="Acknowledge alert"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                  </td>
                </tr>
              ))}
              {!overview?.activeAlerts.length && (
                <tr>
                  <td colSpan={5} className={styles.emptyCell}>
                    No active alerts.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <h2>Recent Orders</h2>
            <span>Latest parsed and manually confirmed requests</span>
          </div>
          <button type="button" onClick={() => openModule("delivery_order")}>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            <span>Open table</span>
          </button>
        </div>
        <div className={styles.tableWrap}>
          <table>
            <thead>
              <tr>
                <th>Order</th>
                <th>Contact</th>
                <th>Client</th>
                <th>Courier</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {(overview?.recentOrders || []).map((order) => (
                <tr key={order.id}>
                  <td>
                    <strong>{order.public_id}</strong>
                    {order.review_required && (
                      <small className={styles.review}>Review required</small>
                    )}
                  </td>
                  <td>{order.contact_name || order.pickup_address || "-"}</td>
                  <td>{order.client_name || "Not selected"}</td>
                  <td>{order.courier_name || "Unassigned"}</td>
                  <td>
                    <span className={styles.status}>
                      {order.status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td>{formatDate(order.created_at)}</td>
                </tr>
              ))}
              {!overview?.recentOrders.length && (
                <tr>
                  <td colSpan={6} className={styles.emptyCell}>
                    No orders yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
